import { lerp, lerpAngle, type Transform } from "@mucki/shared";

export interface TransformSample extends Transform {
  /** Arrival time in milliseconds. */
  time: number;
}

export interface TransformBuffer {
  push(sample: TransformSample): void;
  /** Position at `renderTime`, held rather than extrapolated at the edges. */
  sample(renderTime: number): Transform;
}

/**
 * Remote entities render at a fixed delay behind the newest packet, so a late
 * packet degrades to a brief hold instead of a jump (R13). Only the last two
 * accepted transforms are kept — enough to interpolate, nothing to garbage
 * collect.
 */
export function createTransformBuffer(initial: Transform): TransformBuffer {
  let older: TransformSample | null = null;
  let newer: TransformSample | null = null;

  return {
    push: (sample) => {
      // An out-of-order or duplicate sample would invert the span.
      if (newer && sample.time < newer.time) return;
      older = newer;
      newer = sample;
    },
    sample: (renderTime) => {
      if (!newer) return initial;
      if (!older || renderTime >= newer.time) return newer;
      if (renderTime <= older.time) return older;

      const span = newer.time - older.time;
      const alpha = span > 0 ? (renderTime - older.time) / span : 1;
      return {
        x: lerp(older.x, newer.x, alpha),
        y: lerp(older.y, newer.y, alpha),
        z: lerp(older.z, newer.z, alpha),
        rotationY: lerpAngle(older.rotationY, newer.rotationY, alpha),
      };
    },
  };
}
